// MACHINERY V2 (Stroje) — V3 table: the card-wrapped `.ms-table` (M2.13).
//
// Reference impl: desktop/machinery-v3-table.jsx (V3Body's card + table) +
// desktop/README.md §V3: "Card-wrapped <table class='ms-table'>, radius 10".
//
// >>> NO HORIZONTAL PAGE SCROLL. THE CARD OWNS THE OVERFLOW. <<< The fixed
// columns total 824px (130+38+170+250+120+80+36) before STROJ gets anything, so
// below roughly 1000px of main-column width the table cannot fit. When that
// happens THIS CARD scrolls horizontally — never the page body, and never the
// app shell. `maxWidth: 100%` + `overflowX: auto` on the card is what keeps the
// overflow contained; `minWidth` on the <table> is the floor that makes the card
// scroll instead of crushing the columns.
//
// LAYOUT IS `auto`, NOT `fixed`, AND THAT MATCHES THE ARTBOARD. Under `fixed`
// the browser would hold PRIPOJ. to exactly 38px, leaving 14px of content box
// after the sheet's 12px cell padding — and "PRIPOJ." cannot break into 14px.
// The rendered truth PNG shows that header wrapped over two lines in a column
// visibly wider than 38px, i.e. the prototype's widths are PREFERENCES the
// browser renegotiated. So the widths stay as authored, the layout stays `auto`,
// and every text-heavy cell instead carries its own `maxWidth` (see
// fleetTableCells / fleetTableDataCells) so no single long machine name can
// stretch the table — which is the failure mode `fixed` would have prevented.
//
// The `.ms-table` atom family is imported here, ONCE, as a side effect — the
// M2.6 sheet rule (see machinery-v2-table.scss's header for all four conditions
// and why it is not in the token sheet).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { Machine } from "../../../models/models";
import { MachineryV2ReadinessPresentation } from "../../readiness/readinessPresentation";
import { FleetMenuAction } from "../FleetCardMenu";
import { MachineryV2Telemetry, unconnectedTelemetry } from "../cardStates";
import FleetTableHead from "./FleetTableHead";
import FleetTableRow from "./FleetTableRow";
import { FLEET_TABLE_MIN_WIDTH, FleetTableSort, FleetTableSortColumn } from "./fleetTableSort";
import "./machinery-v2-table.scss";

export interface FleetTableProps {
  /** Already sorted by the view — this component renders, it does not order. */
  machines: Machine[];
  telemetry: Map<string, MachineryV2Telemetry>;
  readiness: Map<string, MachineryV2ReadinessPresentation>;
  /** Session-only NEW highlight, carried for parity with the card + row views. */
  newMachineId: string | null;
  sort: FleetTableSort;
  onSortChange: (column: FleetTableSortColumn) => void;
  buildActions: (m: Machine) => FleetMenuAction[];
  onOpenProfile: (m: Machine) => void;
  onOpenReadiness: (m: Machine) => void;
}

const FleetTable: React.FC<FleetTableProps> = ({
  machines, telemetry, readiness, newMachineId, sort, onSortChange, buildActions,
  onOpenProfile, onOpenReadiness,
}) => (
  <div
    style={{
      background: "var(--card)",
      border: "1px solid var(--border)",
      borderRadius: 10,
      overflowX: "auto",
      overflowY: "hidden",
      maxWidth: "100%",
      minWidth: 0,
    }}
  >
    <table
      className="ms-table"
      style={{ minWidth: FLEET_TABLE_MIN_WIDTH }}
    >
      <FleetTableHead sort={sort} onSortChange={onSortChange} />
      <tbody>
        {machines.map((m) => (
          <FleetTableRow
            key={m.id}
            machine={m}
            telemetry={telemetry.get(m.id) || unconnectedTelemetry()}
            readiness={readiness.get(m.id) || null}
            isNew={m.id === newMachineId}
            actions={buildActions(m)}
            onOpenProfile={() => onOpenProfile(m)}
            onOpenReadiness={() => onOpenReadiness(m)}
          />
        ))}
      </tbody>
    </table>
  </div>
);

export default FleetTable;
